import React from "react";
import { Link } from "react-router-dom";
import { Button, TextField, FormLayout, Text } from "@shopify/polaris";
import AuthContainer from "../../AuthContainer";
import BannerCustom from "components/Banners/BannerCustom";
import passwordIMG from "img/icons/entities/password.svg";
import emailIMG from "img/icons/entities/email.svg";
import eyeOnIMG from "img/icons/misc/eye-on.svg";
import eyeOffIMG from "img/icons/misc/eye-off.svg";
import PageHelmet from "components/PageHelmet";
import useForgotPasswordPage from "../../shared/hooks/useForgotPasswordPage";

export default function ForgotPasswordPage() {
  const {
    t,
    code,
    title,
    description,
    email,
    errorEmail,
    newPassword,
    errorNewPassword,
    newPasswordRepeat,
    errorNewPasswordRepeat,
    serverError,
    setServerError,
    isCodeSent,
    isPasswordChanged,
    isSending,
    isCompleting,
    resendLocked,
    resendSeconds,
    isLinkInvalid,
    showPassword,
    setShowPassword,
    goToSignIn,
    completeErrorMessage,
    completeSuccessMessage,
    serverSuccessMessage,
    setEmail,
    setErrorEmail,
    setNewPassword,
    setErrorNewPassword,
    setNewPasswordRepeat,
    setErrorNewPasswordRepeat,
    doResendCode,
    loginPath,
    doForgotStart,
    doForgotComplete,
    validateEmail,
    validatePassword,
    validatePasswordRepeat,
  } = useForgotPasswordPage();

  const passwordToggle = (
    <img
      src={showPassword ? eyeOffIMG : eyeOnIMG}
      alt={
        showPassword
          ? t("auth.login.hidePassword")
          : t("auth.login.showPassword")
      }
      style={{
        marginTop: 6,
        cursor: "pointer",
        width: "20px",
        height: "20px",
      }}
      onClick={() => setShowPassword((prev) => !prev)}
    />
  );

  return (
    <AuthContainer
      title={title}
      description={isPasswordChanged || isLinkInvalid ? "" : description}
      content={
        <>
          <PageHelmet title={t("forgotPassword.pageTitle")} />
          {serverError ? (
            <BannerCustom
              status="critical"
              message={serverError}
              onDismiss={() => setServerError(false)}
            />
          ) : null}
          {!code && isCodeSent && serverSuccessMessage ? (
            <BannerCustom status="success" message={serverSuccessMessage} />
          ) : null}
          {completeErrorMessage && !isPasswordChanged ? (
            <BannerCustom
              status="critical"
              message={completeErrorMessage}
            />
          ) : null}
          {isPasswordChanged ? (
            <>
              <BannerCustom
                status="success"
                message={
                  completeSuccessMessage || t("forgotPassword.passChanged")
                }
              />
              <div style={{ marginTop: 16 }}>
                <Button variant="primary" onClick={goToSignIn}>
                  {t("forgotPassword.goToSignIn")}
                </Button>
              </div>
            </>
          ) : null}
          {isLinkInvalid && !isPasswordChanged ? (
            <div style={{ marginTop: 16 }}>
              <Link className="Polaris-Link" to={loginPath}>
                {t("forgotPassword.backToSignIn")}
              </Link>
            </div>
          ) : null}
          {!code && !isPasswordChanged ? (
            <form
              autoComplete="on"
              onSubmit={(e) => {
                e.preventDefault();
                if (isCodeSent) {
                  doResendCode(e);
                } else {
                  doForgotStart();
                }
              }}
            >
              <TextField
                name="email"
                type="email"
                prefix={
                  <img src={emailIMG} alt="email" style={{ marginTop: 6 }} />
                }
                label={
                  <Text color="subdued" variant="bodySm">
                    {t("forgotPassword.enterEmailLabel")}
                  </Text>
                }
                placeholder={t("forgotPassword.email")}
                autoComplete="email"
                value={email}
                error={errorEmail}
                onBlur={() => validateEmail(email)}
                onChange={(value) => {
                  setEmail(value);
                  setErrorEmail(false);
                  setServerError(false);
                }}
              />
              <br />
              <div>
                {!isCodeSent ? (
                  <Button
                    variant="primary"
                    submit
                    loading={isSending}
                    disabled={isSending}
                  >
                    {t("forgotPassword.recover")}
                  </Button>
                ) : (
                  <Button
                    submit
                    variant="primary"
                    loading={isSending}
                    disabled={isSending || resendLocked}
                  >
                    {resendLocked && resendSeconds > 0
                      ? t("forgotPassword.resendIn", { seconds: resendSeconds })
                      : t("forgotPassword.resendEmail")}
                  </Button>
                )}
                <div style={{ marginTop: 16 }}>
                  <Link className="Polaris-Link" to={loginPath}>
                    {t("forgotPassword.backToSignIn")}
                  </Link>
                </div>
              </div>
            </form>
          ) : null}
          {code && !isPasswordChanged && !isLinkInvalid ? (
            <form
              autoComplete="on"
              onSubmit={(e) => {
                e.preventDefault();
                doForgotComplete();
              }}
            >
              <FormLayout>
                <TextField
                  name="password"
                  type={showPassword ? "text" : "password"}
                  prefix={
                    <img
                      src={passwordIMG}
                      alt="password"
                      style={{ marginTop: 6 }}
                    />
                  }
                  suffix={passwordToggle}
                  placeholder={t("forgotPassword.password")}
                  label={
                    <Text color="subdued" variant="bodySm">
                      {t("forgotPassword.createTitle")}
                    </Text>
                  }
                  autoComplete="new-password"
                  value={newPassword}
                  error={errorNewPassword}
                  onBlur={() => validatePassword(newPassword)}
                  onChange={(value) => {
                    setNewPassword(value);
                    setErrorNewPassword(false);
                  }}
                />
                <TextField
                  name="password_repeat"
                  type={showPassword ? "text" : "password"}
                  prefix={
                    <img
                      src={passwordIMG}
                      alt="password"
                      style={{ marginTop: 6 }}
                    />
                  }
                  suffix={passwordToggle}
                  placeholder={t("forgotPassword.repeatPlaceholder")}
                  label={
                    <Text color="subdued" variant="bodySm">
                      {t("forgotPassword.repPass")}
                    </Text>
                  }
                  autoComplete="new-password"
                  value={newPasswordRepeat}
                  error={errorNewPasswordRepeat}
                  onBlur={() => validatePasswordRepeat(newPasswordRepeat)}
                  onChange={(value) => {
                    setNewPasswordRepeat(value);
                    setErrorNewPasswordRepeat(false);
                  }}
                />
              </FormLayout>

              <br />
              <div>
                <Button
                  variant="primary"
                  submit
                  loading={isCompleting}
                  disabled={isCompleting}
                >
                  {t("forgotPassword.recover")}
                </Button>
                <div style={{ marginTop: 16 }}>
                  <Link className="Polaris-Link" to={loginPath}>
                    {t("forgotPassword.backToSignIn")}
                  </Link>
                </div>
              </div>
            </form>
          ) : null}
        </>
      }
    />
  );
}
